import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert
} from '@mui/material';

export default function UserRoles() {
    const [selectedEmail, setSelectedEmail] = useState('');
    const [selectedRole, setSelectedRole] = useState('');

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const functions = getFunctions();

    const handleAssignRole = async () => {
        setError('');
        setSuccess('');

        if (!selectedEmail || !selectedRole) {
            setError('Email and role are required');
            return;
        }

        setLoading(true);

        try {
            const setUserRole = httpsCallable(functions, 'setUserRole');
            await setUserRole({
                email: selectedEmail,
                role: selectedRole,
                vendorId: null
            });

            setSuccess(`Role "${selectedRole}" assigned to ${selectedEmail}. User must log out and log back in.`);
            setSelectedEmail('');
            setSelectedRole('');
        } catch (error: any) {
            console.error('Error assigning role:', error);
            setError(error.message || 'Failed to assign role');
        } finally {
            setLoading(false);
        }
    };

    const handleClearRole = async () => {
        setError('');
        setSuccess('');

        if (!selectedEmail) {
            setError('Email is required');
            return;
        }

        if (!window.confirm(`Are you sure you want to clear the role for ${selectedEmail}?`)) {
            return;
        }

        setLoading(true);

        try {
            const setUserRole = httpsCallable(functions, 'setUserRole');
            await setUserRole({
                email: selectedEmail,
                role: null,
                vendorId: null
            });

            setSuccess(`Role cleared for ${selectedEmail}. User must log out and log back in.`);
            setSelectedEmail('');
            setSelectedRole('');
        } catch (error: any) {
            console.error('Error clearing role:', error);
            setError(error.message || 'Failed to clear role');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Admin Role Management
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                Assign admin roles to Firebase Authentication accounts. Users must log out and log back in after role assignment. Partner roles are assigned automatically when creating a partner from the Partners screen.
            </Alert>

            {/* Assign Role Section */}
            <Paper sx={{ p: 3, maxWidth: 600, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Assign Role to Existing User
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <TextField
                        fullWidth
                        label="User Email"
                        type="email"
                        value={selectedEmail}
                        onChange={(e) => setSelectedEmail(e.target.value)}
                        required
                        helperText="Enter the email address from Firebase Authentication"
                        placeholder="user@example.com"
                    />

                    <FormControl fullWidth required>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            label="Role"
                        >
                            <MenuItem value="">
                                <em>Select a role</em>
                            </MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleAssignRole}
                            disabled={loading}
                            fullWidth
                        >
                            {loading ? 'Assigning...' : 'Assign Role'}
                        </Button>

                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleClearRole}
                            disabled={loading}
                            fullWidth
                        >
                            Clear Role
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Creating staff accounts.
                There used to be a "Create New User Account" form here. It
                called createUserWithEmailAndPassword in the browser, which
                swaps the current Firebase session for the newly created user —
                so it signed the admin out of their own session, and the
                follow-up setUserRole call then ran with the new (role-less)
                token and failed permission-denied. Creating the account in the
                console and granting the role above avoids both problems. */}
            <Paper sx={{ p: 3, maxWidth: 600 }}>
                <Typography variant="h6" gutterBottom>
                    Creating a New Staff Account
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Create the account in the Firebase console
                    (Authentication → Users → Add user), then grant it the admin
                    role with the form above. Partner accounts are created from
                    the Partners screen, which provisions the login, the role
                    and the partner record together.
                </Typography>
            </Paper>
        </Box>
    );
}
